/**
 * 크로스블록 선택 — 순수 모델/커버 계산/하이라이트 렌더 (이벤트 배선 없음).
 *
 * 배경: 각 블록의 `.content`가 독립 contenteditable 섬이라 네이티브 드래그가 블록 경계를
 *   넘지 못한다(inline-format/caret-nav 참조). 이 모듈은 (anchorIsland,anchorOffset)→
 *   (focusIsland,focusOffset) 선택을 문서 순서로 정렬해 "커버된 섬 목록"으로 환산하고,
 *   CSS Custom Highlight API로 여러 섬을 한 번에 페인팅한다(blur-highlight와 동일 프리미티브,
 *   이름만 lumir-cross-sel로 분리).
 *
 * 오프셋은 inline-format의 char 유닛 공간(caretToOffset/offsetToCaret)과 정합한다 —
 * 서식 적용은 유닛 수를 바꾸지 않으므로 재렌더 후에도 오프셋이 유효하다.
 *
 * v1 범위: 일반 본문(문단/제목/리스트)만. 표 셀(td.lumir-cell)·단(.lumir-column)·
 *   중첩(.lumir-children) 소속 섬은 커버에서 제외한다(경계 no-op).
 */

import { editableContents } from "./caret-nav.js";
import { caretToOffset, offsetToCaret } from "../inline-format.js";

const HL_NAME = "lumir-cross-sel";
const HL_STYLE_ID = "lumir-cross-sel-style";

const hlSupported =
  typeof CSS !== "undefined" && CSS.highlights && typeof Highlight !== "undefined";

/** 일반 본문 편집 섬인가 — 표/단/중첩 밖의 최상위 `.content[contenteditable=true]`. */
export function isPlainIsland(el) {
  return !!(
    el &&
    el.nodeType === 1 &&
    el.matches &&
    el.matches('.content[contenteditable="true"]') &&
    !el.closest(".lumir-tbl") &&
    !el.closest(".lumir-column") &&
    !el.closest(".lumir-children")
  );
}

/** 노드에서 가장 가까운 일반 본문 섬(없으면 null). */
export function plainIslandOf(node) {
  const el = node && node.nodeType === 1 ? node : node && node.parentElement;
  if (!el || !el.closest) return null;
  const c = el.closest('.content[contenteditable="true"]');
  return isPlainIsland(c) ? c : null;
}

/** 섬 전체의 유닛 길이(끝 오프셋). */
export function contentLen(island) {
  return caretToOffset(island, island, island.childNodes.length);
}

/**
 * 선택 → 커버된 섬 목록(문서 순서). sel = {anchorIsland,anchorOffset,focusIsland,focusOffset}.
 * 반환: [{island, start, end, full}] — 첫/끝은 부분, 중간은 전체. 표/단/중첩 섬은 스킵.
 * anchor/focus 어느 쪽이 앞서든(역방향 드래그) 문서 순서로 정규화한다.
 */
export function coveredIslands(editorEl, sel) {
  if (!editorEl || !sel || !sel.anchorIsland || !sel.focusIsland) return [];
  const list = editableContents(editorEl);
  let ai = list.indexOf(sel.anchorIsland);
  let fi = list.indexOf(sel.focusIsland);
  if (ai < 0 || fi < 0) return [];
  let aOff = sel.anchorOffset;
  let fOff = sel.focusOffset;
  if (ai > fi) {
    [ai, fi] = [fi, ai];
    [aOff, fOff] = [fOff, aOff];
  } else if (ai === fi && aOff > fOff) {
    [aOff, fOff] = [fOff, aOff];
  }
  const out = [];
  for (let i = ai; i <= fi; i++) {
    const island = list[i];
    if (!isPlainIsland(island)) continue; // 표/단/중첩 섬 제외
    const len = contentLen(island);
    const start = i === ai ? aOff : 0;
    const end = i === fi ? fOff : len;
    if (start === end && i !== ai && i !== fi) continue; // 빈 중간 섬은 무해하게 스킵
    out.push({ island, start, end, full: start === 0 && end === len });
  }
  return out;
}

function ensureCrossStyle() {
  if (!hlSupported) return;
  if (typeof document === "undefined" || !document.head) return;
  if (document.getElementById(HL_STYLE_ID)) return;
  try {
    const st = document.createElement("style");
    st.id = HL_STYLE_ID;
    // 네이티브 ::selection과 최대한 유사한 톤(blur-highlight와 동일 색).
    st.textContent = "::highlight(lumir-cross-sel){background:rgba(91,87,214,0.28);}";
    document.head.appendChild(st);
  } catch (_) {}
}

/** 섬 하나에 대한 Range(부분/전체). */
function rangeForCovered(c) {
  const r = document.createRange();
  if (c.full) {
    r.selectNodeContents(c.island);
    return r;
  }
  const a = offsetToCaret(c.island, c.start);
  const b = offsetToCaret(c.island, c.end);
  try {
    r.setStart(a.node, a.offset);
    r.setEnd(b.node, b.offset);
  } catch (_) {
    r.selectNodeContents(c.island);
  }
  return r;
}

/** 커버된 섬들을 하나의 Highlight로 페인팅(미지원 환경은 무시). */
export function paintCrossHighlight(covered) {
  if (!hlSupported) return;
  ensureCrossStyle();
  try {
    if (!covered || !covered.length) {
      CSS.highlights.delete(HL_NAME);
      return;
    }
    const ranges = covered.map(rangeForCovered);
    CSS.highlights.set(HL_NAME, new Highlight(...ranges));
  } catch (_) {}
}

export function clearCrossHighlight() {
  if (!hlSupported) return;
  try {
    CSS.highlights.delete(HL_NAME);
  } catch (_) {}
}

/** 툴바 위치용 — 커버된 섬 Range들의 유니온 BoundingRect(없으면 null). */
export function crossBoundingRect(covered) {
  if (!covered || !covered.length) return null;
  let top = Infinity, left = Infinity, right = -Infinity, bottom = -Infinity;
  let has = false;
  for (const c of covered) {
    const rng = rangeForCovered(c);
    const rect = rng && typeof rng.getBoundingClientRect === "function" ? rng.getBoundingClientRect() : null;
    if (!rect || (!rect.width && !rect.height)) continue;
    has = true;
    top = Math.min(top, rect.top);
    left = Math.min(left, rect.left);
    right = Math.max(right, rect.right);
    bottom = Math.max(bottom, rect.bottom);
  }
  if (!has) return null;
  return { top, left, right, bottom, width: right - left, height: bottom - top };
}
